/* eslint-disable no-console */
import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { addService, editService, removeService } from '../../../../../store/slices/bookingCreationSlice';
import ServiceItem from './ServiceItem';
import { AdditionalService } from '../../../../../types/bookingCreation';
import { useTranslation } from 'react-i18next';

const AdditionalServices = ({ additionalServices }: { additionalServices: AdditionalService[] }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [checkedServices, setCheckedServices] = useState<Record<string, boolean>>(
        additionalServices.reduce<Record<string, boolean>>((acc, service) => {
            acc[service.serviceName] = service.isPaymentCompulsory === true;
            return acc;
        }, {}),
    );

    const [quantities, setQuantities] = useState<Record<string, number>>(
        additionalServices.reduce<Record<string, number>>((acc, service) => {
            acc[service.serviceName] = 1;
            return acc;
        }, {}),
    );

    const handleCheckboxChange = (serviceName: string) => {
        const targetService = additionalServices.find((service) => service.serviceName === serviceName);
        if (targetService?.isPaymentCompulsory) {
            return;
        }
        setCheckedServices((prevState) => {
            const newCheckedState = {
                ...prevState,
                [serviceName]: !prevState[serviceName],
            };

            if (!prevState[serviceName]) {
                dispatch(
                    addService({
                        serviceId: additionalServices.find((service) => service.serviceName === serviceName)?.id!,
                        name: serviceName,
                        quantity: quantities[serviceName],
                        unitPrice: additionalServices.find((service) => service.serviceName === serviceName)?.unitPrice,
                    }),
                );
            } else {
                dispatch(removeService({ name: serviceName }));
            }

            return newCheckedState;
        });
    };

    const handleDecrement = (serviceName: string) => {
        setQuantities((prevQuantities) => {
            const newQuantities = {
                ...prevQuantities,
                [serviceName]: Math.max(1, prevQuantities[serviceName] - 1),
            };
            dispatch(
                editService({
                    serviceId: additionalServices.find((service) => service.serviceName === serviceName)?.id!,
                    name: serviceName,
                    quantity: newQuantities[serviceName],
                    unitPrice: additionalServices.find((service) => service.serviceName === serviceName)?.unitPrice,
                }),
            );
            return newQuantities;
        });
    };

    const handleIncrement = (serviceName: string) => {
        setQuantities((prevQuantities) => {
            const newQuantities = {
                ...prevQuantities,
                [serviceName]: prevQuantities[serviceName] + 1,
            };
            dispatch(
                editService({
                    serviceId: additionalServices.find((service) => service.serviceName === serviceName)?.id!,
                    name: serviceName,
                    quantity: newQuantities[serviceName],
                    unitPrice: additionalServices.find((service) => service.serviceName === serviceName)?.unitPrice,
                }),
            );
            return newQuantities;
        });
    };

    const visibleServices = additionalServices.filter((serviceItem) => serviceItem?.langNames?.en !== 'Internet');
    const hasAnyVisibleService = visibleServices.length > 0;
    const mandatoryServices = visibleServices.filter((s) => s.isPaymentCompulsory === true);
    const optionalServices = visibleServices.filter((s) => !s.isPaymentCompulsory);

    return (
        <Box>
            {!hasAnyVisibleService && (
                <Typography color={'text.secondary'} className={'text-sm'}>
                    {t('create_booking.additional_services.empty_state')}
                </Typography>
            )}
            {hasAnyVisibleService && (
                <>
                    <Typography variant={'subtitle1'} className={'font-medium mt-2 mb-1'}>
                        {t('create_booking.additional_services.mandatory_title')}
                    </Typography>
                    {mandatoryServices.length === 0 ? (
                        <Typography color={'text.secondary'} className={'text-sm mb-2'}>
                            {t('create_booking.additional_services.empty_mandatory')}
                        </Typography>
                    ) : (
                        <Grid container spacing={1} className={'mb-2'}>
                            {mandatoryServices.map((service) => (
                                <Grid item xs={12} sm={6} key={service.id}>
                                    <ServiceItem
                                        service={service}
                                        isChecked={checkedServices[service.serviceName]}
                                        quantity={quantities[service.serviceName]}
                                        onToggle={() => handleCheckboxChange(service.serviceName)}
                                        onIncrement={() => handleIncrement(service.serviceName)}
                                        onDecrement={() => handleDecrement(service.serviceName)}
                                        isMandatory={service.isPaymentCompulsory}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    <Typography variant={'subtitle1'} className={'font-medium mt-3 mb-1'}>
                        {t('create_booking.additional_services.optional_title')}
                    </Typography>
                    {optionalServices.length === 0 ? (
                        <Typography color={'text.secondary'} className={'text-sm'}>
                            {t('create_booking.additional_services.empty_optional')}
                        </Typography>
                    ) : (
                        <Grid container spacing={1}>
                            {optionalServices.map((service) => (
                                <Grid item xs={12} sm={6} key={service.id}>
                                    <ServiceItem
                                        service={service}
                                        isChecked={checkedServices[service.serviceName]}
                                        quantity={quantities[service.serviceName]}
                                        onToggle={() => handleCheckboxChange(service.serviceName)}
                                        onIncrement={() => handleIncrement(service.serviceName)}
                                        onDecrement={() => handleDecrement(service.serviceName)}
                                        isMandatory={service.isPaymentCompulsory}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </>
            )}
        </Box>
    );
};

export default AdditionalServices;
